'use client';

import React, { useState, useEffect, useRef } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { usePathname } from 'next/navigation';

/* ─── Search Panel (dropdown) ─── */
function SearchPanel({
  onClose,
  onSearch,
}: {
  onClose: () => void;
  onSearch: (params: URLSearchParams) => void;
}) {
  const [mode, setMode] = useState<'stays' | 'experiences' | 'deals'>('stays');
  const [destination, setDestination] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [rooms, setRooms] = useState(1);
  const [activeStep, setActiveStep] = useState<'destination' | 'dates' | 'guests'>('destination');

  const suggestions = ['Nairobi, Kenya', 'Mombasa, Kenya', 'Zanzibar, Tanzania', 'Cape Town, South Africa', 'Accra, Ghana', 'Lagos, Nigeria'];

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (destination) params.set('q', destination);
    if (checkIn) params.set('checkIn', checkIn);
    if (checkOut) params.set('checkOut', checkOut);
    if (guests > 1) params.set('guests', String(guests));
    if (rooms > 1) params.set('rooms', String(rooms));
    params.set('mode', mode);
    onSearch(params);
    onClose();
  };

  const modes = [
    { key: 'stays', label: 'Stays', icon: '🏠' },
    { key: 'experiences', label: 'Experiences', icon: '🌍' },
    { key: 'deals', label: 'Deals', icon: '⚡' },
  ] as const;

  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[420px] max-w-[calc(100vw-2rem)] z-50">
      {/* Arrow pointer */}
      <div className="flex justify-center mb-[-1px]">
        <div className="w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45 shadow-[-2px_-2px_4px_rgba(0,0,0,0.04)]" />
      </div>

      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Mode selector strip */}
        <div className="flex border-b border-gray-100">
          {modes.map((m) => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={`flex-1 flex flex-col items-center gap-1 py-3.5 text-xs font-bold transition-all duration-200 ${
                mode === m.key
                  ? 'text-white' :'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
              }`}
              style={mode === m.key ? { background: 'linear-gradient(135deg,#4088C6,#2D6FA8)' } : {}}
            >
              <span className="text-lg leading-none">{m.icon}</span>
              {m.label}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-3">
          {/* Step 1 — Destination */}
          <div
            className={`rounded-2xl border-2 transition-all duration-200 overflow-hidden ${
              activeStep === 'destination' ? 'border-blue-400 shadow-md shadow-blue-100' : 'border-gray-100 hover:border-gray-200'
            }`}
          >
            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
              onClick={() => setActiveStep(activeStep === 'destination' ? 'dates' : 'destination')}
            >
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#4088C6,#2D6FA8)' }}>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-0.5">
                  {mode === 'experiences' ? 'Find Activity' : 'Destination'}
                </div>
                <div className="text-sm font-semibold text-gray-800 truncate">
                  {destination || <span className="text-gray-400 font-normal">{mode === 'experiences' ? 'What do you want to do?' : 'Where are you going?'}</span>}
                </div>
              </div>
              <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${activeStep === 'destination' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {activeStep === 'destination' && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <input
                  type="text"
                  autoFocus
                  placeholder={mode === 'experiences' ? 'Safari, cooking class, diving...' : 'City, region, or property name'}
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && setActiveStep('dates')}
                  className="w-full mt-3 px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                />
                {/* Quick suggestions */}
                <div className="mt-3">
                  <div className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-2">Popular in Africa</div>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => { setDestination(s); setActiveStep('dates'); }}
                        className="text-xs font-medium text-gray-600 bg-gray-100 hover:bg-blue-50 hover:text-blue-700 px-3 py-1.5 rounded-full transition-all duration-150"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Step 2 — Dates */}
          <div
            className={`rounded-2xl border-2 transition-all duration-200 overflow-hidden ${
              activeStep === 'dates' ? 'border-blue-400 shadow-md shadow-blue-100' : 'border-gray-100 hover:border-gray-200'
            }`}
          >
            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
              onClick={() => setActiveStep(activeStep === 'dates' ? 'guests' : 'dates')}
            >
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#F5A623,#E8941A)' }}>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-0.5">Dates</div>
                <div className="text-sm font-semibold text-gray-800">
                  {checkIn && checkOut
                    ? `${checkIn} → ${checkOut}`
                    : checkIn
                    ? `From ${checkIn}`
                    : <span className="text-gray-400 font-normal">When are you going?</span>}
                </div>
              </div>
              <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${activeStep === 'dates' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {activeStep === 'dates' && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-[10px] font-bold tracking-widest uppercase text-gray-400 block mb-1.5">Check in</label>
                    <input
                      type="date"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold tracking-widest uppercase text-gray-400 block mb-1.5">Check out</label>
                    <input
                      type="date"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
                    />
                  </div>
                </div>
                <button
                  onClick={() => setActiveStep('guests')}
                  className="mt-3 w-full text-xs font-bold text-blue-600 hover:text-blue-800 text-right transition-colors"
                >
                  Next: Guests →
                </button>
              </div>
            )}
          </div>

          {/* Step 3 — Guests & Rooms */}
          <div
            className={`rounded-2xl border-2 transition-all duration-200 overflow-hidden ${
              activeStep === 'guests' ? 'border-blue-400 shadow-md shadow-blue-100' : 'border-gray-100 hover:border-gray-200'
            }`}
          >
            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
              onClick={() => setActiveStep(activeStep === 'guests' ? 'destination' : 'guests')}
            >
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#34D399,#059669)' }}>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-0.5">Guests & Rooms</div>
                <div className="text-sm font-semibold text-gray-800">
                  {guests} guest{guests !== 1 ? 's' : ''} · {rooms} room{rooms !== 1 ? 's' : ''}
                </div>
              </div>
              <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${activeStep === 'guests' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {activeStep === 'guests' && (
              <div className="px-4 pb-4 border-t border-gray-100 space-y-4 mt-3">
                {/* Guests row */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Guests</div>
                    <div className="text-xs text-gray-400">Adults & children</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setGuests(Math.max(1, guests - 1))}
                      className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all font-bold text-lg leading-none"
                    >−</button>
                    <span className="w-6 text-center text-sm font-bold text-gray-800">{guests}</span>
                    <button
                      onClick={() => setGuests(guests + 1)}
                      className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all font-bold text-lg leading-none"
                    >+</button>
                  </div>
                </div>
                {/* Rooms row */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Rooms</div>
                    <div className="text-xs text-gray-400">Number of rooms</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setRooms(Math.max(1, rooms - 1))}
                      className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all font-bold text-lg leading-none"
                    >−</button>
                    <span className="w-6 text-center text-sm font-bold text-gray-800">{rooms}</span>
                    <button
                      onClick={() => setRooms(rooms + 1)}
                      className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all font-bold text-lg leading-none"
                    >+</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Search CTA */}
          <button
            onClick={handleSearch}
            className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl font-bold text-sm text-white transition-all duration-200 hover:opacity-90 hover:scale-[1.01] active:scale-[0.99] shadow-lg"
            style={{ background: 'linear-gradient(135deg,#4088C6,#2D6FA8)', boxShadow: '0 6px 20px rgba(64,136,198,0.35)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search {mode === 'stays' ? 'Properties' : mode === 'experiences' ? 'Experiences' : 'Deals'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Explore trigger button (hero / top state) ─── */
function ExploreButton({
  onClick,
  open,
  scrolled,
}: {
  onClick: () => void;
  open: boolean;
  scrolled: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 rounded-2xl px-5 py-3 transition-all duration-300 group ${
        scrolled
          ? open
            ? 'bg-white shadow-xl border-2 border-blue-400 ring-4 ring-blue-100'
            : 'bg-white shadow-lg border border-gray-200 hover:shadow-xl hover:border-blue-300'
          : open
          ? 'bg-white/20 backdrop-blur-md border-2 border-white/60 shadow-xl'
          : 'bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 hover:border-white/50'
      }`}
    >
      {/* Search icon with gradient bg */}
      <div
        className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
        style={{ background: 'linear-gradient(135deg,#4088C6,#2D6FA8)' }}
      >
        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {/* Text content */}
      <div className="text-left">
        <div className={`text-sm font-bold leading-tight ${scrolled ? 'text-gray-800' : 'text-white'}`}>
          Explore Ndoto
        </div>
        <div className={`text-xs leading-tight ${scrolled ? 'text-gray-400' : 'text-white/60'}`}>
          Stays · Experiences · Deals
        </div>
      </div>

      {/* Chevron */}
      <svg
        className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${
          open ? 'rotate-180' : ''
        } ${scrolled ? 'text-gray-400' : 'text-white/60'}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

/* ─── User menu ─── */
function UserMenu({ scrolled }: { scrolled: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const menuItems = [
    { label: 'Log in', href: '/login', bold: true },
    { label: 'Sign up', href: '/signup', bold: false },
    null,
    { label: 'My Trips', href: '/guest-dashboard', bold: false },
    { label: 'My Profile', href: '/guest-profile', bold: false },
    { label: 'Experiences', href: '/experiences', bold: false },
    { label: 'Messages', href: '/messages', bold: false },
    { label: 'Notifications', href: '/notifications', bold: false },
    { label: 'Host Dashboard', href: '/host-dashboard', bold: false },
    { label: 'Become a Host', href: '/host', bold: false },
    null,
    { label: 'Admin', href: '/admin-dashboard', bold: false },
  ];

  return (
    <div ref={ref} className="relative hidden md:block">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2.5 rounded-full px-3 py-2 transition-all duration-200 border ${
          scrolled
            ? 'border-gray-200 bg-white hover:shadow-md'
            : 'border-white/30 bg-white/10 backdrop-blur-sm hover:bg-white/20'
        }`}
      >
        {/* Hamburger */}
        <div className="flex flex-col gap-[4px]">
          <span className={`block w-4 h-[1.5px] rounded-full transition-colors ${scrolled ? 'bg-gray-600' : 'bg-white'}`} />
          <span className={`block w-3 h-[1.5px] rounded-full transition-colors ${scrolled ? 'bg-gray-600' : 'bg-white'}`} />
          <span className={`block w-4 h-[1.5px] rounded-full transition-colors ${scrolled ? 'bg-gray-600' : 'bg-white'}`} />
        </div>
        {/* Avatar with brand blue ring */}
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style={{ background: 'linear-gradient(135deg,#4088C6,#2D6FA8)' }}
        >
          G
        </div>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-3 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100/80 py-2 z-50 overflow-hidden">
          {/* Brand blue top accent */}
          <div className="h-0.5 w-full mb-2" style={{ background: 'linear-gradient(90deg,#4088C6,#5BA3D9,#F5A623)' }} />
          {menuItems.map((item, i) =>
            item === null ? (
              <div key={i} className="border-t border-gray-100 my-1.5 mx-3" />
            ) : (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-all duration-150 hover:bg-blue-50 hover:text-blue-700 group ${
                  item.bold ? 'font-bold text-gray-900' : 'font-medium text-gray-700'
                }`}
              >
                <span className="w-1 h-1 rounded-full bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                {item.label}
              </a>
            )
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Icon button ─── */
function NavIconBtn({
  href,
  label,
  scrolled,
  badge,
  children,
}: {
  href: string;
  label: string;
  scrolled: boolean;
  badge?: boolean;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      aria-label={label}
      className={`hidden md:flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200 relative group ${
        scrolled ? 'text-gray-600 hover:bg-gray-100 hover:text-blue-600' : 'text-white/80 hover:bg-white/15 hover:text-white'
      }`}
    >
      {children}
      {badge && (
        <span
          className="absolute top-1 right-1 w-2 h-2 rounded-full border-2 border-white"
          style={{ background: 'linear-gradient(135deg,#4088C6,#F5A623)' }}
        />
      )}
      {/* Tooltip */}
      <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[10px] font-semibold bg-gray-900 text-white px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
        {label}
      </span>
    </a>
  );
}

/* ─── Main Header ─── */
export default function Header() {
  const pathname = usePathname();

  // Pages with a full-bleed hero image behind the transparent navbar
  const heroPages = ['/', '/home', '/search', '/experiences'];
  const isHeroPage =
    heroPages.includes(pathname) ||
    pathname.startsWith('/experiences/') ||
    pathname.startsWith('/property/') ||
    pathname.startsWith('/destination/');

  const [scrolled, setScrolled] = useState(!isHeroPage);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isHeroPage) {
      setScrolled(true);
      return;
    }
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHeroPage]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (params: URLSearchParams) => {
    window.location.href = `/search?${params.toString()}`;
  };

  return (
    <>
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-[0_2px_20px_rgba(0,0,0,0.08)]'
            : 'bg-transparent'
        }`}
      >
        {/* Brand top gradient line */}
        {scrolled && (
          <div
            className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: 'linear-gradient(90deg,#4088C6,#5BA3D9,#F5A623,#4088C6)' }}
          />
        )}

        <div className={`max-w-7xl mx-auto px-4 sm:px-6 transition-all duration-300 ${scrolled ? 'py-3' : 'py-4'}`}>
          <div className="flex items-center justify-between gap-4">

            {/* ── Logo ── */}
            <div className="flex-shrink-0">
              <a href="/home" className="block">
                <AppLogo
                  variant={scrolled ? 'dark' : 'light'}
                  size={110}
                  className="cursor-pointer"
                />
              </a>
            </div>

            {/* ── Center: Explore button + dropdown panel ── */}
            <div className="hidden md:flex flex-col items-center flex-1 max-w-lg" ref={searchRef}>
              <div className="relative w-full flex justify-center">
                <ExploreButton
                  onClick={() => setSearchOpen(!searchOpen)}
                  open={searchOpen}
                  scrolled={scrolled}
                />
                {searchOpen && (
                  <SearchPanel
                    onClose={() => setSearchOpen(false)}
                    onSearch={handleSearch}
                  />
                )}
              </div>
            </div>

            {/* ── Right nav ── */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Become a Host — pill button */}
              <a
                href="/host"
                className={`hidden lg:flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-all duration-200 ${
                  scrolled
                    ? 'text-gray-700 hover:bg-gray-100 hover:text-blue-600' : 'text-white/90 hover:bg-white/15 hover:text-white'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Host
              </a>

              {/* Messages */}
              <NavIconBtn href="/messages" label="Messages" scrolled={scrolled} badge>
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </NavIconBtn>

              {/* Notifications */}
              <NavIconBtn href="/notifications" label="Notifications" scrolled={scrolled} badge>
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </NavIconBtn>

              {/* Wishlist / saved */}
              <NavIconBtn href="/guest-profile" label="Saved" scrolled={scrolled}>
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </NavIconBtn>

              {/* User menu */}
              <UserMenu scrolled={scrolled} />

              {/* Mobile hamburger */}
              <button
                className={`md:hidden flex flex-col gap-[5px] p-2.5 rounded-full transition-all duration-200 ${
                  scrolled ? 'hover:bg-gray-100' : 'hover:bg-white/15'
                }`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
              >
                <span className={`block w-5 h-[1.5px] rounded-full transition-all duration-300 ${scrolled ? 'bg-gray-700' : 'bg-white'} ${mobileMenuOpen ? 'rotate-45 translate-y-[6.5px]' : ''}`} />
                <span className={`block w-3.5 h-[1.5px] rounded-full transition-all duration-300 ${scrolled ? 'bg-gray-700' : 'bg-white'} ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                <span className={`block w-5 h-[1.5px] rounded-full transition-all duration-300 ${scrolled ? 'bg-gray-700' : 'bg-white'} ${mobileMenuOpen ? '-rotate-45 -translate-y-[6.5px]' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Mobile search bar ── */}
        <div className="md:hidden px-4 pb-3">
          <button
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
            className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 transition-all duration-200 border ${
              mobileSearchOpen
                ? 'bg-white shadow-xl border-blue-400 border-2'
                : 'bg-white/95 backdrop-blur-md shadow-lg border-white/60 hover:shadow-xl'
            }`}
          >
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#4088C6,#2D6FA8)' }}
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-bold text-gray-800">Explore Ndoto</div>
              <div className="text-xs text-gray-400 mt-0.5">Stays · Experiences · Deals</div>
            </div>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${mobileSearchOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Mobile search panel */}
          {mobileSearchOpen && (
            <div className="mt-2 px-1">
              <SearchPanel
                onClose={() => setMobileSearchOpen(false)}
                onSearch={handleSearch}
              />
            </div>
          )}
        </div>

        {/* ── Mobile menu drawer ── */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
            mobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-white/98 backdrop-blur-xl border-t border-gray-100 px-4 py-5 shadow-2xl">
            {/* Brand accent */}
            <div className="h-0.5 w-12 rounded-full mb-5 mx-auto" style={{ background: 'linear-gradient(90deg,#4088C6,#F5A623)' }} />

            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: 'Become a Host', href: '/host', icon: '🏠' },
                { label: 'Host Dashboard', href: '/host-dashboard', icon: '📊' },
                { label: 'My Trips', href: '/guest-dashboard', icon: '✈️' },
                { label: 'My Profile', href: '/guest-profile', icon: '👤' },
                { label: 'Experiences', href: '/experiences', icon: '🌍' },
                { label: 'Messages', href: '/messages', icon: '💬' },
                { label: 'Notifications', href: '/notifications', icon: '🔔' },
                { label: 'Admin', href: '/admin-dashboard', icon: '⚙️' },
              ].map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-all duration-150"
                >
                  <span className="text-base">{item.icon}</span>
                  {item.label}
                </a>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 flex gap-3">
              <a
                href="/login"
                className="flex-1 text-center text-sm font-bold text-gray-800 border-2 border-gray-200 py-2.5 rounded-xl hover:border-blue-400 hover:text-blue-600 transition-all duration-200"
              >
                Log in
              </a>
              <a
                href="/signup"
                className="flex-1 text-center text-sm font-bold text-white py-2.5 rounded-xl transition-all duration-200 hover:opacity-90 shadow-lg"
                style={{ background: 'linear-gradient(135deg,#4088C6,#2D6FA8)' }}
              >
                Sign up
              </a>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}